// src/components/map/AccountsMap.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  MapPin,
  List as ListIcon,
  Layers,
  Route as RouteIcon,
  Filter,
} from "lucide-react";
import SurfaceState from "../common/SurfaceState";

import GoogleMapContainer from "../../lib/maps/GoogleMapContainer";
import { useGeocodedAccounts } from "../../hooks/useGeocodedAccounts";
import { MAP_DEFAULT_CENTER_NA, MAP_DEFAULT_ZOOM_NA } from "../../lib/maps/mapConfig";

// GeoJSON
import usStatesGeoJson from "../../territories/us-states.geo.json";

import { useAccountsMapData } from "./hooks/useAccountsMapData";

import AccountsListPane from "./components/AccountsListPane";
import { TerritoriesPanel } from "./components/TerritoriesPanel";

import {
  accountMatchesTerritory,
  enrichPoints,
  buildStateLayerStyle,
  buildZipHighlightCircles,
  buildTerritoryCircles,
  buildStatsByTerritoryId,
  pointHasTerritoryCoverage,
} from "./utils/mapHelpers";

// ✅ Route modal
import RouteBuilderModal from "../routes/RouteBuilderModal";

export default function AccountsMap({ companyId, initialTab = "accounts", variant = "default" }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [rightTab, setRightTab] = useState(initialTab === "territories" ? "territories" : "accounts");
  const [selectedPointId, setSelectedPointId] = useState(null);

  const [mapCenter, setMapCenter] = useState(null);
  const [mapZoom, setMapZoom] = useState(null);

  const [filters, setFilters] = useState({
    endUserId: "all",
    territoryId: "all",
    samplesOnly: false,
    unassignedOnly: false,
    outsideTerritoryOnly: false,
  });

  const [routeOpen, setRouteOpen] = useState(false);

  const {
    accounts,
    accountsLoading,
    accountsError,

    users,
    usersLoading,
    usersError,

    territories,
    territoriesLoading,
    territoriesError,

    sampleData,
    samplesLoading,
    samplesError,

    myProfile,
    isAdmin,
  } = useAccountsMapData(companyId);

  // Map: open account
  const handleOpenAccount = (accountId) => {
    if (!accountId) return;
    const base = location.pathname.startsWith("/app") ? "/app/accounts" : "/accounts";
    navigate(`${base}?edit=${encodeURIComponent(accountId)}`);
  };

  // Map: open sample request
  const handleOpenSampleRequest = (requestId) => {
    if (!requestId) return;
    const base = location.pathname.startsWith("/app") ? "/app/sample-orders" : "/sample-orders";
    navigate(`${base}?requestId=${encodeURIComponent(requestId)}`);
  };

  // Points (geocoded) derived from visible accounts
  const { points, loading: pointsLoading, error: pointsError } = useGeocodedAccounts(
    companyId,
    accountsLoading ? null : accounts
  );

  const accountsById = useMemo(() => {
    const map = {};
    (accounts || []).forEach((acc) => {
      map[acc.id] = acc;
    });
    return map;
  }, [accounts]);

  const usersForCompany = useMemo(() => {
    const list = users || [];
    const nonCustomers = list.filter((u) => String(u.role || "").toLowerCase() !== "customer");

    // Admins: show all users in this company for filtering
    if (isAdmin) return nonCustomers.filter((u) => (u.companyId || null) === companyId);

    // End users: just keep what the hook returned (usually "me")
    return nonCustomers;
  }, [users, companyId, isAdmin]);

  useEffect(() => {
    if (isAdmin) return;
    setFilters((prev) => {
      const nextEndUserId = "all";
      const territoryAllowed =
        prev.territoryId === "all" || territories.some((t) => t.id === prev.territoryId);
      const nextTerritoryId = territoryAllowed ? prev.territoryId : "all";

      if (prev.endUserId === nextEndUserId && prev.territoryId === nextTerritoryId) {
        return prev;
      }

      return {
        ...prev,
        endUserId: nextEndUserId,
        territoryId: nextTerritoryId,
      };
    });
  }, [isAdmin, territories]);

  const usersById = useMemo(() => {
    const m = new Map();
    (usersForCompany || []).forEach((u) => m.set(u.id, u));
    return m;
  }, [usersForCompany]);

  const getUserLabel = (u) => u?.name || u?.displayName || u?.fullName || u?.email || "Unnamed user";

  const territoriesById = useMemo(() => {
    const m = new Map();
    (territories || []).forEach((t) => m.set(t.id, t));
    return m;
  }, [territories]);

  const enrichedPoints = useMemo(() => {
    return enrichPoints(points, accountsById, usersById, sampleData);
  }, [points, accountsById, usersById, sampleData]);

  const filteredPoints = useMemo(() => {
    let list = enrichedPoints;

    if (filters.endUserId !== "all") {
      list = list.filter((p) => p.assignedToId === filters.endUserId);
    }

    if (filters.territoryId !== "all") {
      const territory = territoriesById.get(filters.territoryId) || null;
      list = territory ? list.filter((p) => accountMatchesTerritory(p, territory)) : [];
    }

    if (filters.samplesOnly) list = list.filter((p) => p.hasSampleOrder);
    if (filters.unassignedOnly) list = list.filter((p) => !p.assignedToId);
    if (filters.outsideTerritoryOnly) {
      list = list.filter((p) => !pointHasTerritoryCoverage(p, territories));
    }

    return list;
  }, [enrichedPoints, filters, territories, territoriesById]);

  // if selected point falls out of filtered list, clear it
  useEffect(() => {
    if (!selectedPointId) return;
    if (!filteredPoints.some((p) => p.id === selectedPointId)) setSelectedPointId(null);
  }, [filteredPoints, selectedPointId]);

  const selectedPoint = useMemo(
    () => filteredPoints.find((p) => p.id === selectedPointId) || null,
    [filteredPoints, selectedPointId]
  );

  const highlightTerritoryId = useMemo(() => {
    if (filters.territoryId !== "all") return filters.territoryId;
    return selectedPoint?.effectiveTerritoryId || null;
  }, [filters.territoryId, selectedPoint]);

  const selectedTerritory = useMemo(() => {
    if (!highlightTerritoryId) return null;
    return territoriesById.get(highlightTerritoryId) || null;
  }, [highlightTerritoryId, territoriesById]);

  const statesDataLayerStyle = useMemo(
    () => buildStateLayerStyle(selectedTerritory),
    [selectedTerritory]
  );

  const zipHighlightCircles = useMemo(
    () => buildZipHighlightCircles(selectedTerritory, enrichedPoints),
    [selectedTerritory, enrichedPoints]
  );

  const territoryCircles = useMemo(
    () => buildTerritoryCircles(territories, enrichedPoints),
    [territories, enrichedPoints]
  );

  const circlesForMap = useMemo(() => {
    return selectedTerritory ? [...territoryCircles, ...zipHighlightCircles] : territoryCircles;
  }, [territoryCircles, zipHighlightCircles, selectedTerritory]);

  const statsByTerritoryId = useMemo(() => {
    return buildStatsByTerritoryId(territories, enrichedPoints);
  }, [territories, enrichedPoints]);

  const territorySummary = useMemo(() => {
    const totalAccounts = enrichedPoints.length;
    const unassignedAccounts = enrichedPoints.filter((p) => !p.assignedToId).length;
    const uncoveredAccounts = enrichedPoints.filter((p) => !pointHasTerritoryCoverage(p, territories)).length;
    const sampledAccounts = enrichedPoints.filter((p) => Number(p.sampleCount || 0) > 0).length;
    const convertedAccounts = enrichedPoints.filter((p) => Number(p.convertedCount || 0) > 0).length;

    return {
      totalAccounts,
      unassignedAccounts,
      uncoveredAccounts,
      sampledAccounts,
      convertedAccounts,
    };
  }, [enrichedPoints, territories]);

  // ✅ IMPORTANT FIX:
  // Only auto-center/zoom once per selection (or if that account's lat/lng changes),
  // so the user can pan/drag freely after the zoom-in.
  const lastFocusRef = useRef({ id: null, lat: null, lng: null });

  useEffect(() => {
    if (!selectedPointId) {
      lastFocusRef.current = { id: null, lat: null, lng: null };
      setMapCenter(null);
      setMapZoom(null);
      return;
    }

    const p = filteredPoints.find((x) => x.id === selectedPointId) || null;
    const lat = p ? Number(p.lat) : NaN;
    const lng = p ? Number(p.lng) : NaN;

    if (!p || !Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const prev = lastFocusRef.current;
    const changed = prev.id !== selectedPointId || prev.lat !== lat || prev.lng !== lng;

    if (!changed) return; // don't keep re-centering while selected

    lastFocusRef.current = { id: selectedPointId, lat, lng };
    setMapCenter({ lat, lng });
    setMapZoom(12);
  }, [selectedPointId, filteredPoints]);

  const handleResetView = () => {
    setSelectedPointId(null);
    setMapCenter(null);
    setMapZoom(null);
    setFilters((prev) => ({ ...prev, territoryId: "all" }));
  };

  const markers = useMemo(() => {
    return filteredPoints.map((p) => ({
      id: p.id,
      lat: p.lat,
      lng: p.lng,
      pinColor: p.pinColor,
      sampleCount: p.sampleCount || 0,
      convertedCount: p.convertedCount || 0,
      sampleSummaries: p.sampleSummaries || [],
      title:
        p.name ||
        (p.city || p.state
          ? `${p.name || "Account"} – ${[p.city, p.state].filter(Boolean).join(", ")}`
          : "Account"),
    }));
  }, [filteredPoints]);

  const anyLoading =
    pointsLoading || accountsLoading || usersLoading || territoriesLoading || samplesLoading;

  const combinedError =
    pointsError || accountsError || usersError || territoriesError || samplesError || "";

  const isTerritoriesView = variant === "territories";

  // ✅ Prefill for modal (based on map state)
  const routePrefill = useMemo(() => {
    const territoryId = filters.territoryId !== "all" ? filters.territoryId : "";
    const sampleFilter = filters.samplesOnly ? "with" : "any";
    const accountIds = Array.from(new Set((filteredPoints || []).map((p) => p.id).filter(Boolean)));

    return {
      territoryId,
      sampleFilter,
      // show the route builder ONLY the accounts currently visible on the map
      accountIds,
      // keep it smart: if you’re filtered down already, don’t auto-pick via territory (avoid overriding)
      autoPickFromTerritory: false,
    };
  }, [filters.territoryId, filters.samplesOnly, filteredPoints]);

  // ✅ Tiny chips next to button so you know what’s “active”
  const activeFilterChips = useMemo(() => {
    const chips = [];

    if (filters.territoryId !== "all") {
      const t = territoriesById.get(filters.territoryId);
      chips.push({
        key: "territory",
        label: t?.name || "Territory",
      });
    }

    if (filters.endUserId !== "all") {
      const u = usersById.get(filters.endUserId);
      chips.push({
        key: "user",
        label: u ? `User: ${getUserLabel(u)}` : "User filter",
      });
    }

    if (filters.samplesOnly) {
      chips.push({ key: "samples", label: "Samples only" });
    }
    if (filters.unassignedOnly) {
      chips.push({ key: "unassigned", label: "Unassigned only" });
    }
    if (filters.outsideTerritoryOnly) {
      chips.push({ key: "outside", label: "Outside territory only" });
    }

    if (!chips.length) chips.push({ key: "none", label: "No filters" });

    return chips;
  }, [filters, territoriesById, usersById]);

  return (
    <>
      <div className={`map-screen-shell bg-white rounded-xl shadow-sm border border-gray-200 ${isTerritoriesView ? "p-3" : "p-4"}`}>
        <div className={`flex ${isTerritoriesView ? "items-center" : "items-start"} justify-between gap-3 mb-3`}>
          {!isTerritoriesView ? (
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-blue-500" />
                Account Map
              </h2>
              <p className="text-xs text-gray-500">
                View accounts and build a route based on what you’re currently looking at.
              </p>
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-1 mr-1">
              <span className="inline-flex items-center gap-1 text-[10px] text-gray-500">
                <Filter className="w-3 h-3" />
              </span>
              {activeFilterChips.map((c) => (
                <span
                  key={c.key}
                  className="text-[10px] px-2 py-1 rounded-full border border-gray-200 bg-gray-50 text-gray-700"
                  title="Active map filter"
                >
                  {c.label}
                </span>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 flex-shrink-0">
            {!isTerritoriesView ? (
              <div className="hidden md:flex items-center gap-1 mr-1">
                <span className="inline-flex items-center gap-1 text-[10px] text-gray-500">
                  <Filter className="w-3 h-3" />
                </span>
                {activeFilterChips.map((c) => (
                  <span
                    key={c.key}
                    className="text-[10px] px-2 py-1 rounded-full border border-gray-200 bg-gray-50 text-gray-700"
                    title="Active map filter"
                  >
                    {c.label}
                  </span>
                ))}
              </div>
            ) : null}

            <button
              type="button"
              onClick={() => setRouteOpen(true)}
              className="text-xs px-2 py-1 rounded-md bg-blue-600 text-white hover:bg-blue-700 inline-flex items-center gap-1"
              title="Create a route in Google Maps using current map filters"
            >
              <RouteIcon className="w-3.5 h-3.5" />
              Create route
            </button>

            <button
              type="button"
              onClick={handleResetView}
              className="text-xs px-2 py-1 border border-gray-200 rounded-md text-gray-600 hover:bg-gray-50"
            >
              Reset view
            </button>
          </div>
        </div>

        {/* Mobile chips under header */}
        <div className="md:hidden flex flex-wrap gap-1 mb-2">
          {activeFilterChips.map((c) => (
            <span
              key={c.key}
              className="text-[10px] px-2 py-1 rounded-full border border-gray-200 bg-gray-50 text-gray-700"
            >
              {c.label}
            </span>
          ))}
        </div>

        {combinedError && (
          <SurfaceState
            tone="error"
            compact
            className="mb-2"
            title="Map data issue"
            description={combinedError}
          />
        )}

        <div className="flex flex-col lg:flex-row gap-4 h-[72vh]">
          <div className="flex-1 h-64 lg:h-full rounded-lg overflow-hidden border border-gray-200 relative">
            {anyLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 p-4">
                <div className="w-full max-w-sm">
                  <SurfaceState
                    tone="loading"
                    compact
                    title="Loading locations"
                    description="Pulling in accounts, territories, and sample context for the map."
                  />
                </div>
              </div>
            )}

            {!anyLoading && !combinedError && filteredPoints.length === 0 ? (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/75 p-4">
                <div className="w-full max-w-sm">
                  <SurfaceState
                    tone="empty"
                    compact
                    title="Nothing matches this map view"
                    description="Try resetting the map filters or adding accounts with addresses so they can appear here."
                  />
                </div>
              </div>
            ) : null}

            <GoogleMapContainer
              markers={markers}
              polygons={[]}
              circles={circlesForMap}
              center={mapCenter || MAP_DEFAULT_CENTER_NA}
              zoom={mapZoom || MAP_DEFAULT_ZOOM_NA}
              selectedMarkerId={selectedPointId}
              onMarkerClick={(markerObj) => {
                if (markerObj?.id) setSelectedPointId(markerObj.id);
              }}
              onOpenAccount={handleOpenAccount}
              onOpenSampleRequest={handleOpenSampleRequest}
              dataGeoJson={usStatesGeoJson}
              dataLayerStyle={statesDataLayerStyle}
            />
          </div>

          <div className="w-full lg:w-80 h-full flex flex-col rounded-lg border border-gray-200 overflow-hidden">
            <div className="flex border-b border-gray-200 text-xs">
              <button
                type="button"
                onClick={() => setRightTab("accounts")}
                className={`flex-1 flex items-center justify-center gap-1 px-2 py-2 font-medium ${
                  rightTab === "accounts"
                    ? "bg-blue-50 text-blue-700 border-b-2 border-blue-500"
                    : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                }`}
              >
                <ListIcon className="w-3 h-3" />
                Accounts
                {filteredPoints.length > 0 && (
                  <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-white border border-gray-200">
                    {filteredPoints.length}
                  </span>
                )}
              </button>

              <button
                type="button"
                onClick={() => setRightTab("territories")}
                className={`flex-1 flex items-center justify-center gap-1 px-2 py-2 font-medium ${
                  rightTab === "territories"
                    ? "bg-blue-50 text-blue-700 border-b-2 border-blue-500"
                    : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                }`}
              >
                <Layers className="w-3 h-3" />
                Territories
                {filters.territoryId !== "all" && (
                  <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-white border border-gray-200">
                    1
                  </span>
                )}
              </button>
            </div>

            <div className="flex-1 overflow-hidden bg-white">
              {rightTab === "accounts" ? (
                <AccountsListPane
                  points={filteredPoints}
                  loading={anyLoading}
                  error={combinedError}
                  selectedPointId={selectedPointId}
                  onSelect={setSelectedPointId}
                  users={usersForCompany}
                  territories={territories}
                  filters={filters}
                  onFiltersChange={setFilters}
                  getUserLabel={getUserLabel}
                  isAdmin={isAdmin}
                />
              ) : (
                <TerritoriesPanel
                  territories={territories}
                  loading={territoriesLoading}
                  error={territoriesError}
                  selectedTerritoryId={filters.territoryId}
                  statsByTerritoryId={statsByTerritoryId}
                  summary={territorySummary}
                  filters={filters}
                  onFiltersChange={setFilters}
                  onSelectTerritory={(id) => {
                    setFilters((prev) => ({ ...prev, territoryId: id ? id : "all" }));
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Route modal mounted here with smart prefill */}
      <RouteBuilderModal
        open={routeOpen}
        onClose={() => setRouteOpen(false)}
        companyId={companyId}
        me={myProfile}
        prefill={routePrefill}
      />
    </>
  );
}
